// apps/client/src/pages/leads/edm/EdmDomainPage.tsx
// /leads/edm/domain: the company's eDM sending domain (Phase 1c S6). Enter a
// domain, create it, publish the DNS records shown, refresh the status.
// Company-wide roles only (route wrapped in RequireEdmImportAccess; the
// server enforces it again). Adding and removing a domain is super_admin
// only (server-enforced; the buttons are hidden for other roles). The server
// is the authority for every check.

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle, Globe, Info, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/common/PageHeader';
import { Spinner } from '../../../components/common/Spinner';
import { Skeleton } from '../../../components/common/SkeletonLoader';
import { useEdmDomain } from '../../../hooks/useEdmDomain';
import type { EdmDomainState, EdmDomainStatus } from '../../../hooks/useEdmDomain';
import { EDM_UI, edmErrorMessage, edmErrorReason } from '../../../utils/edm';

interface OutletCtx {
  userData?: { companyId?: string; role?: string };
}

const STATUS_COPY: Record<EdmDomainStatus, { label: string; chip: string }> = {
  creating: { label: 'Setting up', chip: 'border-gray-500/40 bg-gray-500/10 text-gray-300' },
  create_failed: { label: 'Setup failed', chip: 'border-red-500/40 bg-red-500/10 text-red-300' },
  not_started: { label: 'Waiting for DNS', chip: 'border-amber-500/40 bg-amber-500/10 text-amber-300' },
  pending: { label: 'Checking DNS', chip: 'border-amber-500/40 bg-amber-500/10 text-amber-300' },
  verified: { label: 'Verified', chip: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' },
  failed: { label: 'Verification failed', chip: 'border-red-500/40 bg-red-500/10 text-red-300' },
  temporary_failure: { label: 'Temporarily failing', chip: 'border-amber-500/40 bg-amber-500/10 text-amber-300' },
};

/** Domain-specific refusals; anything else goes through the shared eDM mapper. */
const DOMAIN_REASON_COPY: Record<string, string> = {
  domain_invalid: 'Enter a valid domain name, for example mail.yourbusiness.com.au.',
  domain_not_allowed: 'This domain cannot be used as a sending domain.',
  domain_free_mail: 'A free email provider domain (Gmail, Outlook, Yahoo, iCloud, BigPond) cannot be used as a sending domain.',
  domain_taken: 'This domain is already in use by another account.',
  domain_already_claimed: 'Your company already has a sending domain. Remove it before adding another.',
  domain_create_in_progress: 'This domain is still being set up. Try again in a few minutes.',
  domain_create_failed: 'The domain could not be added. Check the name and try again.',
  domain_limit_reached: 'The sending-domain limit has been reached. Contact VALUTTEN support.',
  domain_rate_limited: 'Too many attempts. Wait a minute (or until tomorrow for adding a domain) and try again.',
  domain_not_found: 'No sending domain has been added.',
  domain_not_created: 'The domain has not been created yet. Add it again.',
  domain_refresh_failed: 'The domain status could not be read. Try again shortly.',
  domain_remove_failed: 'The domain could not be removed. Try again shortly.',
  domain_verified: 'A verified sending domain cannot be removed here. Contact VALUTTEN support.',
  domain_company_wide_only: 'The sending domain is managed by company-wide roles only.',
  domain_super_admin_only: 'Only a super admin can add or remove the sending domain.',
};

function domainErrorMessage(err: unknown): string {
  return DOMAIN_REASON_COPY[edmErrorReason(err)] ?? edmErrorMessage(err);
}

type Busy = 'create' | 'refresh' | 'remove' | null;

const EdmDomainPage: React.FC = () => {
  const ctx = useOutletContext<OutletCtx>();
  const companyId = ctx?.userData?.companyId;
  const isSuperAdmin = ctx?.userData?.role === 'super_admin';
  const api = useEdmDomain(companyId);

  const [state, setState] = useState<EdmDomainState | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState<Busy>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      setState(await api.getDomain());
    } catch (err) {
      setError(domainErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [api, companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (kind: Exclude<Busy, null>, fn: () => Promise<unknown>) => {
    setBusy(kind);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(domainErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const create = () =>
    run('create', async () => {
      const res = await api.createDomain(name.trim());
      setState((s) => (s ? { ...s, domain: res.domain } : { domain: res.domain, envDomain: null, isProd: false }));
      setName('');
    });

  const refresh = () =>
    run('refresh', async () => {
      const res = await api.refreshDomain();
      setState((s) => (s ? { ...s, domain: res.domain } : s));
    });

  const remove = () =>
    run('remove', async () => {
      await api.removeDomain();
      setConfirmRemove(false);
      setState((s) => (s ? { ...s, domain: null } : s));
    });

  const domain = state?.domain ?? null;
  const status = domain ? STATUS_COPY[domain.status] ?? STATUS_COPY.pending : null;
  const canRefresh = domain !== null && domain.resendDomainId !== null && domain.status !== 'creating';
  const canRemove = isSuperAdmin && domain !== null && domain.status !== 'verified';

  return (
    <div className="space-y-6">
      <Link to="/leads/edm" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 hover:text-gray-200">
        <ArrowLeft className="h-4 w-4" /> Back to campaigns
      </Link>
      <PageHeader
        icon={Globe}
        title="Sending domain"
        subtitle="Send eDM campaigns from your own business domain"
        onRefresh={() => void load()}
        isRefreshing={loading}
      />

      {error && (
        <div className={`${EDM_UI.errorBanner} flex items-center gap-3`} role="alert">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
        </div>
      )}

      {loading && !state ? (
        <div className={EDM_UI.card} data-testid="edm-domain-skeleton">
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <>
          {!domain && (
            <div className={EDM_UI.card}>
              <h2 className={EDM_UI.heading}>
                <Globe className={EDM_UI.headingIcon} /> Add your sending domain
              </h2>
              <div className={`${EDM_UI.infoBanner} flex gap-2`}>
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  {state?.isProd
                    ? 'Campaigns can only be sent once your own domain is verified.'
                    : `Until your domain is verified, campaigns are sent from ${state?.envDomain ?? "VALUTTEN's eDM address"}.`}{' '}
                  We recommend a subdomain such as mail.yourbusiness.com.au so your everyday email is not affected.
                </span>
              </div>
              {isSuperAdmin ? (
                <form
                  className="flex flex-col gap-3 sm:flex-row sm:items-end"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (name.trim() && busy === null) void create();
                  }}
                >
                  <div className="flex-1">
                    <label htmlFor="edm-domain-name" className={EDM_UI.label}>
                      Domain
                    </label>
                    <input
                      id="edm-domain-name"
                      className={EDM_UI.input}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="mail.yourbusiness.com.au"
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </div>
                  <button type="submit" className={EDM_UI.primaryButton} disabled={!name.trim() || busy !== null}>
                    {busy === 'create' ? <Spinner size="sm" /> : <Plus className="h-4 w-4" />} Create
                  </button>
                </form>
              ) : (
                <p className="text-sm text-gray-400">Ask a super admin to add your sending domain.</p>
              )}
            </div>
          )}

          {domain && status && (
            <div className={EDM_UI.card}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className={EDM_UI.heading}>
                  <Globe className={EDM_UI.headingIcon} /> {domain.name}
                  <span className={`${EDM_UI.chip} ${status.chip}`} data-testid="edm-domain-status">
                    {domain.status === 'verified' && <CheckCircle className="mr-1 h-3 w-3" />}
                    {status.label}
                  </span>
                </h2>
                <div className="flex flex-wrap gap-2">
                  {canRefresh && (
                    <button type="button" className={EDM_UI.secondaryButton} onClick={() => void refresh()} disabled={busy !== null}>
                      {busy === 'refresh' ? <Spinner size="sm" /> : <RefreshCw className="h-4 w-4" />} Refresh status
                    </button>
                  )}
                  {canRemove && !confirmRemove && (
                    <button type="button" className={EDM_UI.secondaryButton} onClick={() => setConfirmRemove(true)} disabled={busy !== null}>
                      <Trash2 className="h-4 w-4" /> Remove
                    </button>
                  )}
                </div>
              </div>

              {domain.status === 'create_failed' && (
                <div className={`${EDM_UI.warnBanner} flex gap-2`}>
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>
                    Setting up this domain failed{domain.error ? ` (${domain.error})` : ''}.
                    {isSuperAdmin ? ' Remove it and add it again, or contact VALUTTEN support.' : ' Ask a super admin to retry.'}
                  </span>
                </div>
              )}
              {domain.status === 'verified' ? (
                <div className={`${EDM_UI.infoBanner} flex gap-2`}>
                  <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>Your domain is verified. New campaigns are sent from no-reply@{domain.name}.</span>
                </div>
              ) : (
                domain.records.length > 0 && (
                  <p className="text-sm text-gray-300">
                    Add these DNS records at your domain provider, then select Refresh status. DNS changes can take up to 72 hours.
                  </p>
                )
              )}

              {confirmRemove && (
                <div className={`${EDM_UI.warnBanner} flex flex-wrap items-center gap-3`} role="alertdialog" aria-label="Remove sending domain">
                  <span className="flex-1">Remove {domain.name}? You can add a domain again afterwards.</span>
                  <button type="button" className={EDM_UI.secondaryButton} onClick={() => setConfirmRemove(false)} disabled={busy !== null}>
                    Cancel
                  </button>
                  <button type="button" className={EDM_UI.dangerButton} onClick={() => void remove()} disabled={busy !== null}>
                    {busy === 'remove' ? <Spinner size="sm" /> : <Trash2 className="h-4 w-4" />} Remove domain
                  </button>
                </div>
              )}

              {domain.records.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm" aria-label="DNS records">
                    <thead className="text-xs uppercase text-gray-400">
                      <tr>
                        <th className="px-3 py-2">Type</th>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Value</th>
                        <th className="px-3 py-2">Priority</th>
                        <th className="px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {domain.records.map((r) => (
                        <tr key={`${r.record}:${r.type}:${r.name}`}>
                          <td className="px-3 py-2 font-mono text-gray-100">{r.type}</td>
                          <td className="px-3 py-2 font-mono text-gray-200 break-all">{r.name}</td>
                          <td className="px-3 py-2 font-mono text-gray-200 break-all">{r.value}</td>
                          <td className="px-3 py-2 text-gray-300">{r.priority ?? ''}</td>
                          <td className="px-3 py-2">
                            <span className={`${EDM_UI.chip} ${(STATUS_COPY[r.status as EdmDomainStatus] ?? STATUS_COPY.pending).chip}`}>
                              {(STATUS_COPY[r.status as EdmDomainStatus] ?? STATUS_COPY.pending).label}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EdmDomainPage;
